/**
 * Copying an assistant answer so it can be pasted to a customer in any channel. The clipboard gets
 * two versions: the rendered HTML (an email client or a rich editor keeps the links clickable) and
 * plain text in which each link is written out as "title (url)", so a plain-text field (an SMS, a
 * ticket in another tool) still carries every address.
 */

/** A link target: no spaces, and parentheses only as balanced pairs (`page_(v2)`), one level deep. */
const TARGET = String.raw`((?:[^()\s]|\([^()\s]*\))+)`;
const IMAGE = new RegExp(String.raw`!\[([^\]]*)\]\(${TARGET}(?:\s+"[^"]*")?\)`, 'g');
const LINK = new RegExp(String.raw`\[([^\]]+)\]\(${TARGET}(?:\s+"[^"]*")?\)`, 'g');
/** Stands in for a written-out link while formatting marks are stripped; never typed by anyone. */
const SLOT = (index: number): string => `\uE000${index}\uE000`;

/**
 * Markdown to readable plain text: links spelled out as "title (url)", formatting marks dropped.
 * Links are set aside first and put back last, so stripping `__` or `*` can never reach inside an
 * address (`https://help.acme.test/a__b__c` stays as it is).
 */
export function markdownToPlainText(markdown: string): string {
  const links: string[] = [];
  const keep = (written: string): string => {
    links.push(written);
    return SLOT(links.length - 1);
  };
  const text = markdown
    .replace(IMAGE, (_m, alt: string, url: string) => keep(alt ? `${alt} (${url})` : url))
    .replace(LINK, (_m, label: string, url: string) =>
      keep(label === url ? url : `${label} (${url})`),
    )
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/^>\s?/gm, '')
    .replace(/(\*\*|__)(.+?)\1/g, '$2')
    .replace(/(^|[^*\w])\*(?!\s)([^*\n]+?)\*(?!\w)/g, '$1$2')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/^[ \t]*[-*+][ \t]+/gm, '- ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  return text.replace(/\uE000(\d+)\uE000/g, (_m, index: string) => links[Number(index)] ?? '');
}

/**
 * Put an answer on the clipboard. `html` is the rendered answer when the caller has it. Falls back
 * to plain text where rich clipboard writes are not supported. Resolves false when nothing could be
 * written (no clipboard access).
 */
export async function copyReply(markdown: string, html?: string): Promise<boolean> {
  const text = markdownToPlainText(markdown);
  const clipboard = typeof navigator === 'undefined' ? undefined : navigator.clipboard;
  if (!clipboard) return false;
  try {
    if (html && typeof ClipboardItem !== 'undefined' && typeof clipboard.write === 'function') {
      await clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([text], { type: 'text/plain' }),
        }),
      ]);
      return true;
    }
    await clipboard.writeText(text);
    return true;
  } catch {
    try {
      await clipboard.writeText(text);
      return true;
    } catch {
      return false;
    }
  }
}
